import { Inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";

import { ShopStock } from "@hermes/api-model-shop";
import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { Logger } from "@hermes/logger";
import { LoadGeoLibrariesService } from "@hermes/map-utils";
import { ShopLocatorFacade } from "@hermes/states/shop-locator";
import { AnalyticsService } from "@hermes/utils/analytics";
import { LOGGER } from "@hermes/web-logger";

import { ProductLocatorCallToActionEvent } from "./product-locator-call-to-action.event";
import {
    fetchShopsStock,
    fetchShopsStockFailure,
    fetchShopsStockSuccess,
    launchProductLocator,
} from "./product-locator.actions";
import { ProductLocatorService } from "./product-locator.service";

/**
 * Effects on product page to call services or external API
 */
@Injectable()
export class ProductLocatorEffects {
    public fetchShopsStock$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchShopsStock),
            switchMap(({ skus }) =>
                this.productLocatorService.getStocksByShop(skus).pipe(
                    map((stockByShop: ShopStock[]) =>
                        fetchShopsStockSuccess({
                            // eslint-disable-next-line unicorn/no-array-reduce, unicorn/prefer-object-from-entries
                            shopsStock: stockByShop.reduce(
                                (accumulator, item) => ({
                                    ...accumulator,
                                    [item.cegidId]: item,
                                }),
                                {},
                            ),
                        }),
                    ),
                    catchError((error) =>
                        of(
                            fetchShopsStockFailure({
                                errorMessage: error.message,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
    public launchProductLocator$ = createEffect(() =>
        this.actions$.pipe(
            ofType(launchProductLocator),
            map(({ skus }) => {
                if (skus.length === 0) {
                    return fetchShopsStockFailure({
                        errorMessage:
                            "Error while opening the Product Locator : no SKU was provided",
                    });
                }
                // send event when user click on find-in-store CTA
                this.analyticsService.sendData(
                    new ProductLocatorCallToActionEvent({ skus }),
                );
                // Dispatch stores recovering action if the store has not a correct data
                this.shopLocatorFacade.fetchShops({
                    countryCode: this.locale.countryCode,
                    lang: this.locale.langCode,
                    cegidId: "exists",
                    productLocator: true,
                });
                this.loadGeoLibrariesService.autoLoad();
                return fetchShopsStock({ skus });
            }),
        ),
    );

    public fetchShopsStockFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fetchShopsStockFailure),
                tap(({ errorMessage }) => {
                    this.logger.error(errorMessage);
                }),
            ),
        { dispatch: false },
    );

    constructor(
        private productLocatorService: ProductLocatorService,
        private shopLocatorFacade: ShopLocatorFacade,
        private loadGeoLibrariesService: LoadGeoLibrariesService,
        private analyticsService: AnalyticsService,
        private actions$: Actions,
        @Inject(LOGGER) private logger: Logger,
        @Inject(LOCALE) private locale: Locale,
    ) {}
}
